/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import NdsTable from './NdsTable';
import SearchBox from 'components/common/SearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { NDSType, ndsBreadcrumbItems } from 'data/nds';
import ndsService from 'service/nds';
import ButtonRefresh from 'components/base/ButtonRefresh';

const Nds = () => {
  const { t, i18n } = useTranslation();

  //Get NDS
  const { data, refetch } = useQuery(['GET_NDS'], () => {
    const res = ndsService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => res?.vats);
    return res;
  });

  const ndsData: NDSType[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          country: items?.country?.name?.[i18n?.language],
          nds: items?.value,
          status: items?.status === true ? t('active') : t('blocked'),
          guid: items?.id
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const ndsTableColumns: ColumnDef<NDSType>[] = [
    {
      accessorKey: 'country',
      header: t('country'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'nds',
      header: t('vat_values'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          [t('active')]: { bg: '#BBFBD0', text: '#119C2B' },
          [t('blocked')]: { bg: '#FDDFDD', text: '#F2271C' }
        };
        /* prettier-ignore */
        const backgroundColor =
         defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex">
            <p
              style={{
                background: backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {status}
            </p>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: ndsData,
    columns: ndsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };
  return (
    <div>
      <PageBreadcrumb items={ndsBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex gap-2 align-items-center mb-5">
          <h2>{t('vat')}</h2>
          <ButtonRefresh onRefresh={() => refetch()} />
        </div>
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <Row className="g-3">
              <Col xs="auto">
                <SearchBox
                  placeholder={t('search')}
                  onChange={handleSearchInputChange}
                />
              </Col>
            </Row>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <NdsTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Nds;
